'use client';

import * as React from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { setUser } from '@/store/reducers/user-slice';
import { zodResolver } from '@hookform/resolvers/zod';
import { Alert, Box, Divider, FormControl, FormHelperText, InputLabel, OutlinedInput, Stack } from '@mui/material';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { Eye } from '@phosphor-icons/react/dist/ssr/Eye';
import { EyeSlash } from '@phosphor-icons/react/dist/ssr/EyeSlash';
import { WindowsLogo, WindowsLogoIcon } from '@phosphor-icons/react/dist/ssr/WindowsLogo';
import { Controller, useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { z as zod } from 'zod';

import { authClient } from '@/lib/auth/custom/client';

// import { toast } from '@/components/core/toaster';

// interface OAuthProvider {
//   id: 'microsoft' | 'discord';
//   name: string;
//   logo: string;
// }

// const oAuthProviders = [
//   { id: 'microsoft', name: 'Microsoft', logo: '/assets/mslogo.png' },
//   // { id: 'discord', name: 'Discord', logo: '/assets/logo-discord.svg' },
// ] satisfies OAuthProvider[];

const schema = zod.object({
  email: zod.string().min(1, { message: 'Email is required' }).email(),
  password: zod.string().min(1, { message: 'Password is required' }),
});

type Values = zod.infer<typeof schema>;

const defaultValues = { email: '', password: '' } satisfies Values;

export function SignInForm(): React.JSX.Element {
  const router = useRouter();
  const searchParams = useSearchParams();
  const callbackUrl = searchParams.get('callbackUrl');
  const dispatch = useDispatch();
  const [showPassword, setShowPassword] = React.useState<boolean>();

  const [isPending, setIsPending] = React.useState<boolean>(false);

  const {
    control,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<Values>({ defaultValues, resolver: zodResolver(schema) });

  // const onAuth = React.useCallback(async (providerId: OAuthProvider['id']): Promise<void> => {
  //   setIsPending(true);

  //   const { userData, error } = await authClient.signInWithOAuth({ provider: providerId });

  //   if (error || !userData) {
  //     setIsPending(false);
  //     toast.error(error);
  //     return;
  //   }

  //   dispatch(setUser(userData));
  //   if (callbackUrl) {
  //     router.replace(callbackUrl);
  //   } else {
  //     router.replace('/');
  //   }

  //   setTimeout(() => {
  //     setIsPending(false);
  //   }, 8000);

  //   // Redirect to OAuth provider
  // }, []);

  const onSubmit = React.useCallback(
    async (values: Values): Promise<void> => {
      setIsPending(true);

      const { error, userData } = await authClient.signInWithPassword(values);

      if (error || !userData) {
        setError('root', { type: 'server', message: error });
        setIsPending(false);
        return;
      }

      // Refresh the auth state
      // await checkSession?.();

      // UserProvider, for this case, will not refresh the router
      // After refresh, GuestGuard will handle the redirect
      dispatch(setUser(userData));
      if (callbackUrl) {
        router.replace(callbackUrl);
      } else {
        router.replace('/');
      }
      // router.refresh();
    },
    [router, setError]
  );

  return (
    <Stack spacing={4}>
      {/* <div>
        <Box component={RouterLink} href={paths.home} sx={{ display: 'inline-block', fontSize: 0 }}>
          <DynamicLogo colorDark="light" colorLight="dark" height={32} width={122} />
        </Box>
      </div> */}
      <Stack spacing={1}>
        <Typography variant="h5">Sign in</Typography>
        {/* <Typography color="text.secondary" variant="body2">
          Don&apos;t have an account?{' '}
          <Link component={RouterLink} href={paths.auth.custom.signUp} variant="subtitle2">
            Sign up
          </Link>
        </Typography> */}
      </Stack>
      <Stack spacing={3}>
        {/* <Stack spacing={2}>
          {oAuthProviders.map(
            (provider): React.JSX.Element => (
              <Button
                color="secondary"
                disabled={isPending}
                endIcon={<Box alt="" component="img" height={20} src={provider.logo} width={20} />}
                // endIcon={<WindowsLogo />}
                key={provider.id}
                onClick={(): void => {
                  onAuth(provider.id).catch(() => {
                    // noop
                  });
                }}
                variant="outlined"
              >
                Continue with {provider.name}
              </Button>
            )
          )}
        </Stack> */}
        {/* <Divider>or</Divider> */}
        <Stack spacing={2}>
          {/* <form> */}
          <form onSubmit={handleSubmit(onSubmit)}>
            <Stack spacing={2}>
              <Controller
                control={control}
                name="email"
                render={({ field }) => (
                  <FormControl error={Boolean(errors.email)}>
                    <InputLabel>Email address</InputLabel>
                    <OutlinedInput {...field} type="email" />
                    {errors.email ? <FormHelperText>{errors.email.message}</FormHelperText> : null}
                  </FormControl>
                )}
              />
              <Controller
                control={control}
                name="password"
                render={({ field }) => (
                  <FormControl error={Boolean(errors.password)}>
                    <InputLabel>Password</InputLabel>
                    <OutlinedInput
                      {...field}
                      endAdornment={
                        showPassword ? (
                          <Eye
                            cursor="pointer"
                            fontSize="var(--icon-fontSize-md)"
                            onClick={(): void => {
                              setShowPassword(false);
                            }}
                          />
                        ) : (
                          <EyeSlash
                            cursor="pointer"
                            fontSize="var(--icon-fontSize-md)"
                            onClick={(): void => {
                              setShowPassword(true);
                            }}
                          />
                        )
                      }
                      type={showPassword ? 'text' : 'password'}
                    />
                    {errors.password ? <FormHelperText>{errors.password.message}</FormHelperText> : null}
                  </FormControl>
                )}
              />
              {errors.root ? <Alert color="error">{errors.root.message}</Alert> : null}
              <Button disabled={isPending} type="submit" variant="contained">
                Sign in
              </Button>
            </Stack>
          </form>

          <Stack sx={{ mt: 6, gap: 1 }}>
            <Divider>OR</Divider>
            <Button
              disabled={isPending}
              type="button"
              variant="contained"
              color="secondary"
              onClick={() => {
                window.location.href = '/api/auth/login/microsoft';
              }}
              endIcon={<WindowsLogoIcon />}
            >
              Continue with Microsoft
            </Button>
            <Button
              disabled={isPending}
              type="button"
              variant="contained"
              color="secondary"
              onClick={() => {
                window.location.href = '/api/auth/login/google';
              }}
              endIcon={<Box alt="" component="img" height={20} src="/assets/logo-google.svg" width={20} />}
            >
              Continue with Google
            </Button>
          </Stack>

          {/* <div>
            <Link component={RouterLink} href={paths.auth.custom.resetPassword} variant="subtitle2">
              Forgot password?
            </Link>
          </div> */}
        </Stack>
      </Stack>
    </Stack>
  );
}
